import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  Box,
  Stack,
  Button,
  Drawer,
  Divider,
  TextField,
  Typography,
  IconButton,
  CircularProgress,
  FormHelperText,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

const EMPTY = { title: '', en: '', ka: '' };

const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ indent: '-1' }, { indent: '+1' }],
    ['link', 'blockquote', 'code-block'],
    [{ align: [] }],
    ['clean'],
  ],
};

const isQuillEmpty = (html) =>
  !html || String(html).replace(/<(.|\n)*?>/g, '').replace(/&nbsp;/g, '').trim() === '';

export default function TcCreateDrawer({ open, onClose, onSubmit, submitting }) {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open) {
      setForm(EMPTY);
      setErrors({});
    }
  }, [open]);

  const handleTitleChange = (event) => {
    const { value } = event.target;
    setForm((prev) => ({ ...prev, title: value }));
    if (errors.title) {
      setErrors((prev) => ({ ...prev, title: undefined }));
    }
  };

  const handleQuillChange = (name) => (value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const validate = () => {
    const next = {};
    if (!form.title.trim()) next.title = 'Title is required';
    if (isQuillEmpty(form.en)) next.en = 'English text is required';
    if (isQuillEmpty(form.ka)) next.ka = 'Georgian text is required';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSubmit = () => {
    if (submitting) return;
    if (!validate()) return;
    onSubmit({
      title: form.title.trim(),
      en: form.en,
      ka: form.ka,
    });
  };

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={submitting ? undefined : onClose}
      PaperProps={{
        sx: { width: { xs: '100%', md: 720 } },
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ p: 2, borderBottom: (theme) => `1px solid ${theme.palette.divider}` }}
      >
        <Typography variant="h6">New Terms and Conditions</Typography>
        <IconButton onClick={onClose} disabled={submitting}>
          <CloseIcon />
        </IconButton>
      </Stack>

      <Box sx={{ p: 3, flex: 1, overflowY: 'auto' }}>
        <Stack spacing={2.5}>
          <TextField
            fullWidth
            name="title"
            label="Title"
            value={form.title}
            onChange={handleTitleChange}
            error={Boolean(errors.title)}
            helperText={errors.title}
            disabled={submitting}
          />

          <Divider textAlign="left">
            <Typography variant="caption" color="text.secondary">
              CONTENT
            </Typography>
          </Divider>

          <Box>
            <Typography
              variant="subtitle2"
              gutterBottom
              color={errors.en ? 'error' : 'text.primary'}
            >
              English (EN)
            </Typography>
            <Box
              sx={{
                '& .ql-container': { minHeight: 220, fontSize: 14 },
                '& .ql-editor': { minHeight: 220 },
                ...(errors.en && {
                  '& .ql-toolbar, & .ql-container': {
                    borderColor: 'error.main',
                  },
                }),
                pointerEvents: submitting ? 'none' : 'auto',
                opacity: submitting ? 0.6 : 1,
              }}
            >
              <ReactQuill
                theme="snow"
                value={form.en}
                onChange={handleQuillChange('en')}
                modules={QUILL_MODULES}
                placeholder="Paste or type the English terms and conditions…"
                readOnly={submitting}
              />
            </Box>
            {errors.en && (
              <FormHelperText error sx={{ mx: 1.5 }}>
                {errors.en}
              </FormHelperText>
            )}
          </Box>

          <Box>
            <Typography
              variant="subtitle2"
              gutterBottom
              color={errors.ka ? 'error' : 'text.primary'}
            >
              Georgian (KA)
            </Typography>
            <Box
              sx={{
                '& .ql-container': { minHeight: 220, fontSize: 14 },
                '& .ql-editor': { minHeight: 220 },
                ...(errors.ka && {
                  '& .ql-toolbar, & .ql-container': {
                    borderColor: 'error.main',
                  },
                }),
                pointerEvents: submitting ? 'none' : 'auto',
                opacity: submitting ? 0.6 : 1,
              }}
            >
              <ReactQuill
                theme="snow"
                value={form.ka}
                onChange={handleQuillChange('ka')}
                modules={QUILL_MODULES}
                placeholder="ჩაწერეთ ქართული ტექსტი…"
                readOnly={submitting}
              />
            </Box>
            {errors.ka && (
              <FormHelperText error sx={{ mx: 1.5 }}>
                {errors.ka}
              </FormHelperText>
            )}
          </Box>
        </Stack>
      </Box>

      <Stack
        direction="row"
        justifyContent="flex-end"
        spacing={1.5}
        sx={{
          p: 2,
          borderTop: (theme) => `1px solid ${theme.palette.divider}`,
        }}
      >
        <Button
          variant="outlined"
          color="inherit"
          onClick={onClose}
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          startIcon={
            submitting ? <CircularProgress size={16} color="inherit" /> : null
          }
        >
          {submitting ? 'Creating…' : 'Create'}
        </Button>
      </Stack>
    </Drawer>
  );
}

TcCreateDrawer.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  submitting: PropTypes.bool,
};

TcCreateDrawer.defaultProps = {
  submitting: false,
};
